/**
 * 管理后台监控入口：在 app.tsx 调用 setupWebMonitoring 注册全局 error/click 监听。
 */
import { history } from '@umijs/max';
import { configureMonitoring, reportMonitoringEvent } from './core';

let initialized = false;

function collapseWhitespace(value?: string | null) {
  return value?.replace(/\s+/g, ' ').trim() || '';
}

function resolveTrackableElement(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return null;
  }
  return target.closest<HTMLElement>('a, button, [role="button"], .ant-btn, .ant-menu-item, [data-track-click]');
}

function resolveElementLabel(element: HTMLElement) {
  const candidate =
    element.dataset.trackClick ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    element.innerText ||
    element.textContent;
  return collapseWhitespace(candidate).slice(0, 80);
}

export function setupWebMonitoring() {
  configureMonitoring({
    consoleOnly: process.env.NODE_ENV !== 'production',
  });

  if (typeof window === 'undefined' || initialized) {
    return;
  }
  initialized = true;

  window.addEventListener('error', (event) => {
    void reportMonitoringEvent({
      eventType: 'runtime_error',
      eventName: 'window_error',
      level: 'error',
      page: history.location.pathname,
      route: history.location.pathname,
      message: event.message,
      stack: event.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    void reportMonitoringEvent({
      eventType: 'promise_rejection',
      eventName: 'window_unhandled_rejection',
      level: 'error',
      page: history.location.pathname,
      route: history.location.pathname,
      message: String(event.reason ?? 'Unhandled promise rejection'),
    });
  });

  document.addEventListener('click', (event) => {
    const element = resolveTrackableElement(event.target);
    if (!element) {
      return;
    }

    const label = resolveElementLabel(element);
    const href = element.getAttribute('href') || undefined;
    void reportMonitoringEvent({
      eventType: 'user_action',
      eventName: 'element_click',
      level: 'info',
      page: history.location.pathname,
      route: `${window.location.pathname}${window.location.search}`,
      extra: {
        label: label || element.tagName.toLowerCase(),
        tagName: element.tagName.toLowerCase(),
        href,
      },
    });
  }, true);
}

export { reportMonitoringEvent };
